import { useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import NavBar from "../../components/navBar/navBar";
import Loader from "../../components/Loader";
import useGetStaffNotifications from "../../hooks/useGetStaffNotifications";
import useMarkNotificationReadMutation from "../../hooks/useMarkNotificationReadMutation";
import useMarkAllNotificationsReadMutation from "../../hooks/useMarkAllNotificationsReadMutation";

dayjs.extend(relativeTime);

const headerCellSx = {
  backgroundColor: (theme) => theme.palette.grey[100],
  fontWeight: "bold",
  textTransform: "uppercase",
  fontSize: "0.85rem",
  color: "#424242",
  whiteSpace: "nowrap",
};

const StaffNotificationPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [readFilter, setReadFilter] = useState("");

  const { data = { results: [], count: 0 }, isLoading } = useGetStaffNotifications({ page });
  const { mutate: markRead } = useMarkNotificationReadMutation();
  const { mutate: markAllRead, isPending: markingAll } = useMarkAllNotificationsReadMutation();

  const notifications = data.results.filter((n) => {
    if (readFilter === "unread") return !n.is_read;
    if (readFilter === "read") return n.is_read;
    return true;
  });

  const handleRowClick = (notification) => {
    if (!notification.is_read) markRead(notification.id);
    if (notification.link) navigate(notification.link);
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <NavBar active="notifications" />

      <Paper elevation={1} sx={{ mt: 3, p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Notifications</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={readFilter}
                onChange={(e) => {
                  setReadFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="unread">Unread</MenuItem>
                <MenuItem value="read">Read</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              disabled={markingAll}
              onClick={() => markAllRead()}
            >
              Mark all read
            </Button>
          </Stack>
        </Stack>

        {isLoading && <Loader />}

        <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx}>Title</TableCell>
                <TableCell sx={headerCellSx}>Message</TableCell>
                <TableCell sx={headerCellSx}>Subject</TableCell>
                <TableCell sx={headerCellSx}>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading &&
                notifications.map((n) => (
                  <TableRow
                    key={n.id}
                    hover
                    onClick={() => handleRowClick(n)}
                    sx={{
                      cursor: n.link ? "pointer" : "default",
                      backgroundColor: n.is_read ? "transparent" : "#fff5f7",
                    }}
                  >
                    <TableCell>
                      {n.is_read ? (
                        <Chip label="Read" size="small" variant="outlined" />
                      ) : (
                        <Chip label="Unread" size="small" color="error" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={n.is_read ? "normal" : "bold"}>
                        {n.title}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" noWrap>
                        {n.message}
                      </Typography>
                    </TableCell>
                    <TableCell>{n.site_paticipant_id ?? "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(n.created_at).fromNow()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

              {!isLoading && notifications.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No notifications found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 2 }}>
          <Typography variant="body2">
            Showing {notifications.length} of {data.count}
          </Typography>
          <Pagination
            color="primary"
            page={page}
            count={Math.ceil(data.count / 10)}
            onChange={(_, p) => setPage(p)}
          />
        </Stack>
      </Paper>
    </Box>
  );
};

export default StaffNotificationPage;
